// OP-3605 — Regras puras das ocorrências, espelho de `domain/vinculo.ts`
// (`NATUREZA_DE`, `Resolucao`, `RESOLUCOES_QUE_SILENCIAM`). Gates visuais.
import { NaturezaOcorrencia, Ocorrencia, OcorrenciaDerivada, OcorrenciaGravada, Resolucao, TipoOcorrencia } from '../../../../types/estoque'

export const NATUREZA_UI: Record<NaturezaOcorrencia, { bg: string; color: string; label: string; explica: string }> = {
  // Algo está errado: o razão e a venda discordam, e alguém precisa regularizar.
  ALERTA: { bg: '#FEE2E2', color: '#991B1B', label: 'Alerta', explica: 'O razão e a venda discordam. Alguém precisa descobrir qual dos dois corrigir.' },
  // Nada está errado: falta alguém decidir o que fazer.
  DECISAO: { bg: '#FEF3C7', color: '#92400E', label: 'Decisão', explica: 'Nada está errado. Falta alguém decidir o que fazer.' },
}

export const TIPO_OCORRENCIA_UI: Record<TipoOcorrencia, { label: string; explica: string }> = {
  DETENTOR_DIVERGENTE: {
    label: 'Detentor divergente',
    explica: 'A peça vendeu para um produtor diferente do que o razão diz que está com ela. Trocou de mão sem passar pela logística.',
  },
  VENDA_SEM_REMESSA: {
    label: 'Venda sem remessa',
    explica: 'A peça vendeu e não há remessa aberta que explique onde ela está.',
  },
  VIGENCIA_VENCE_COM_VENDA_ATIVA: {
    label: 'Vigência vence com venda ativa',
    explica: 'O prazo acaba antes do fim do evento que ainda está vendendo. Prorrogue ou cobre o retorno.',
  },
  RETORNO_VENCIDO: {
    label: 'Retorno vencido',
    explica: 'Passou do prazo combinado e o equipamento não voltou.',
  },
  PEDIDO_COM_SALDO_PENDENTE: {
    label: 'Pedido com saldo pendente',
    explica: 'Saiu menos do que foi pedido. Decidir é no próprio pedido: segunda remessa, esperar transferência ou encerrar o saldo.',
  },
}

/**
 * A linha é derivada de um pedido?
 *
 * A derivada vem com `id: null` de propósito: não há registro para resolver —
 * ela some sozinha quando o saldo zera. As duas formas de linha não
 * compartilham campos, então a tela decide por aqui o que renderizar.
 */
export const ehDerivada = (o: Ocorrencia): o is OcorrenciaDerivada => o.id == null

export const ehGravada = (o: Ocorrencia): o is OcorrenciaGravada => o.id != null

/** Só a gravada se resolve; a derivada se resolve mexendo no pedido. */
export function podeResolver(o: Ocorrencia, temCapacidade: boolean): boolean {
  return temCapacidade && ehGravada(o)
}

export const RESOLUCOES: Array<{ resolucao: Resolucao; rotulo: string; efeito: string }> = [
  { resolucao: 'REGULARIZADO', rotulo: 'Regularizado', efeito: 'O fato foi corrigido. Se não tiver sido, a varredura reabre a pendência.' },
  { resolucao: 'PRORROGADO', rotulo: 'Prorrogado', efeito: 'O prazo mudou. Com prazo novo, uma pendência nova pode aparecer.' },
  { resolucao: 'COBRADO', rotulo: 'Cobrado', efeito: 'Já foi cobrado: a varredura não reabre esta pendência enquanto o prazo for o mesmo.' },
  { resolucao: 'IGNORADO', rotulo: 'Ignorado', efeito: 'Decidiu-se conviver com isso: a varredura não reabre enquanto o prazo for o mesmo.' },
]

/**
 * Espelho de `RESOLUCOES_QUE_SILENCIAM`: só COBRADO e IGNORADO grudam, e
 * apenas enquanto o limite não mudar. As outras duas valem se o fato mudar —
 * se não mudou, a linha volta amanhã, e é honesto avisar isso antes.
 */
export const SILENCIAM: Resolucao[] = ['COBRADO', 'IGNORADO']
export const resolucaoSilencia = (r: Resolucao | ''): boolean => !!r && SILENCIAM.indexOf(r as Resolucao) >= 0

export function impedimentoResolver(resolucao: Resolucao | '', temCapacidade: boolean): string | null {
  if (!temCapacidade) return 'Resolver ocorrência exige a permissão "Ocorrências".';
  if (!resolucao) return 'Escolha o que foi feito.'
  return null
}

/** Alerta antes de decisão, como a consulta já devolve — a tela preserva a hierarquia. */
export function ordenarPorNatureza(linhas: Ocorrencia[]): Ocorrencia[] {
  return linhas.slice().sort((a, b) => {
    if (a.natureza === b.natureza) return 0
    return a.natureza === 'ALERTA' ? -1 : 1
  })
}
